import {
    IonButton,
    IonCard,
    IonCol,
    IonContent,
    IonItem,
    IonLabel,
    IonList,
    IonPage,
    IonRefresher,
    IonRefresherContent,
    IonRow,
    IonText,
    IonThumbnail,
    useIonAlert,
    useIonRouter,
    useIonViewDidEnter,
} from "@ionic/react";
import React, { FC } from "react";
import { HeaderComponent } from "../../../components";
import { useTranslation } from "react-i18next";
import { routes } from "../../../route/constants";
import { useStoreRefreshSubscriptions } from "../../../components/hooks/refreshStoreSubscriptions";
import ProBadge from "../../../assets/images/pro-badge.png";
import PlusBadge from "../../../assets/images/upgrade-plus-and-pro/plus-badge.png";
import { SubscriptionItems } from "refsix-js-models";
import moment from "moment";
import { isAndroid, isIos } from "../../../services/platformDetection";
import {
    trackPageView,
    trackProTeaser,
} from "../../../services/analytics/analyticsService";
import { URLS } from "../../../constants/urls";
import {
    useIsBasic,
    useStripeInvoice,
    useStripeSchedule,
    useSubscription,
} from "../../../components/hooks";
import {
    isUserPlusEligible,
    refreshSubscriptions,
} from "../../../services/subscriptionService";
import { isSubscriptionPaid } from "refsix-core";
import { SubscriptionSchedule } from "./subscription/subscriptionSchedule";
import "./subscriptions.css";
import { reactivateSubscription } from "../../../services/StripeService";

export const ReactiveSubscriptionButton: FC<{
    provider: string;
    sub: SubscriptionItems;
}> = ({ provider, sub }) => {
    const [showConfirmation] = useIonAlert();
    const { t } = useTranslation();
    if (provider !== "stripe") {
        return null;
    }

    return (
        <IonButton
            fill={"outline"}
            onClick={() =>
                showConfirmation({
                    header: t("cancellation.reactivate.buttonText"),
                    subHeader: t("cancellation.reactivate.alert.subheader"),
                    message: t("cancellation.reactivate.alert.message", {
                        date: moment(sub.validUntil).format("LLL"),
                    }),
                    buttons: [
                        {
                            text: t("general.noThanks"),
                            role: "cancel",
                        },
                        {
                            text: t("cancellation.reactivate.buttonText"),
                            role: "confirm",
                            handler: async () => {
                                await reactivateSubscription();
                                console.log("Reactivating subscription...");
                            },
                        },
                    ],
                    cssClass: "reactivate-subscription-alert",
                })
            }
        >
            {t("cancellation.reactivate.buttonText")}
        </IonButton>
    );
};
export const Subscriptions: FC = () => {
    const { t } = useTranslation();
    const route = useIonRouter();
    const subscriptionState = useSubscription();
    const [schedule] = useStripeSchedule();

    const isBasic = useIsBasic();

    const refresh = useStoreRefreshSubscriptions();

    useIonViewDidEnter(() => {
        refreshSubscriptions().catch((error: any) => {
            console.log(error);
        });
    });
    const navigateToCancellationFeedback = (_provider: string) => {
        trackPageView(routes.subscriptionCancel + "/refsix");
        route.push(routes.moreSupportContact, "forward");
    };

    const renderCancelSubscriptionButton = (provider: string) => {
        if (subscriptionState && isSubscriptionPaid(provider)) {
            return (
                <IonCol size="12" className="ion-col-no-padding">
                    <IonButton
                        data-testid="cancel-subscription-button"
                        fill="outline"
                        style={{ marginTop: 15 }}
                        onClick={() => navigateToCancellationFeedback(provider)}
                    >
                        {t("myaccount.cancelSubscription")}
                    </IonButton>
                </IonCol>
            );
        }
    };

    const renderSubscription = (
        subscription: SubscriptionItems,
        index: number
    ) => {
        return (
            <IonItem
                data-testid={`subscription-item${index}`}
                key={index}
                lines={
                    subscriptionState &&
                    subscriptionState.subscriptions.length > 1
                        ? "full"
                        : "none"
                }
            >
                {subscription.planFeatures === "pro" && (
                    <IonThumbnail slot="start">
                        <img style={{ width: "49px" }} src={ProBadge} />
                    </IonThumbnail>
                )}
                {subscription.planFeatures === "basic" && (
                    <IonThumbnail slot="start">
                        <img style={{ width: "49px" }} src={PlusBadge} />
                    </IonThumbnail>
                )}
                <IonRow style={{ padding: 5 }}>
                    <IonCol size="12" className="ion-col-no-padding">
                        <IonText
                            className="text-medium"
                            data-testid={`sub${index}-plan-description`}
                            style={{
                                color: "white",
                                fontWeight: "bold",
                                whiteSpace: "initial",
                            }}
                        >
                            {subscription.planDescription}
                        </IonText>
                    </IonCol>
                    <IonCol size="12" className="ion-col-no-padding">
                        <IonText
                            className="text-medium"
                            style={{ textTransform: "uppercase" }}
                        >
                            {subscription.provider}
                        </IonText>
                    </IonCol>
                    <IonCol size="12" className="ion-col-no-padding">
                        <IonText>
                            {t("billing.expiry")}:{" "}
                            {moment(subscription.validUntil).format("LLL")}
                        </IonText>
                        {subscription.provider === "stripe" && (
                            <SubscriptionSchedule />
                        )}
                    </IonCol>
                    {schedule?.renewal_behavior === "cancel" ? (
                        <ReactiveSubscriptionButton
                            sub={subscription}
                            provider={subscription.provider}
                        />
                    ) : (
                        renderCancelSubscriptionButton(subscription.provider)
                    )}
                </IonRow>
            </IonItem>
        );
    };

    return (
        <IonPage>
            <HeaderComponent
                title={t("myaccount.subscriptionsHeader")}
                showBackButton
                defaultHref={routes.moreProfile}
                titleTestId={"more-subscriptions-page-header"}
            />
            <IonContent>
                <IonCard>
                    <IonList lines="inset">
                        {subscriptionState &&
                            subscriptionState.subscriptions.length > 0 &&
                            subscriptionState.subscriptions.map(
                                renderSubscription
                            )}

                        {!subscriptionState ||
                            (!subscriptionState.subscriptions.length && (
                                <>
                                    <IonItem data-testid="free-user-sub">
                                        <IonText>{t("myaccount.free")}</IonText>
                                    </IonItem>
                                    <IonItem>
                                        <IonButton
                                            data-testid="subs-free-user-upgrade-btn"
                                            color="primary"
                                            // button
                                            onClick={() => {
                                                if (isIos() || isAndroid()) {
                                                    if (
                                                        isUserPlusEligible() &&
                                                        !isBasic
                                                    ) {
                                                        route.push(
                                                            routes.upgrade +
                                                                "/plus"
                                                        );
                                                        trackProTeaser(
                                                            "subscriptions",
                                                            "basic"
                                                        );
                                                    } else if (
                                                        !isUserPlusEligible() ||
                                                        isBasic
                                                    ) {
                                                        route.push(
                                                            routes.upgrade +
                                                                "/pro"
                                                        );
                                                        trackProTeaser(
                                                            "subscriptions",
                                                            "pro"
                                                        );
                                                    }
                                                } else {
                                                    window.open(
                                                        URLS.REFSIX_ACCOUNT +
                                                            "?utm_source=refsix&utm_medium=app&utm_campaign=in-app-mobile-web&utm_id=in-app-mobile-web",
                                                        "_system"
                                                    );
                                                }
                                            }}
                                        >
                                            <IonLabel>
                                                {t("menu.upgrade")}
                                            </IonLabel>
                                        </IonButton>
                                    </IonItem>
                                </>
                            ))}
                    </IonList>
                </IonCard>

                <IonCard>
                    <IonButton
                        expand="block"
                        onClick={refresh}
                        data-testid="restore-purchases-btn"
                    >
                        {t("myaccount.refreshButton")}
                    </IonButton>
                </IonCard>
            </IonContent>
        </IonPage>
    );
};
